import { BaseController } from '~/utils/base-controller';
import {
	GridStack,
} from 'gridstack';
import { QueryBuilder } from '@knitiv/api-client-javascript';
import APIV5 from '~/models/api';
import Utils from '~/scripts/services/utils';
import { cloneDeep } from 'lodash-es';
import { element } from 'angular';
import { kebabCase } from 'change-case';

export const SETTINGS_ID = 'layout';
export const METADATA_ID = 'metadata';
export const TARGET_KID = 'K_NODE;UICPNT_HOME';

/**
 * @typedef {Object} Widget
 * @property {string} type
 * @property {string} id
 * @property {any} [params]
 * @property {string} [title]
 * @property {import('gridstack').GridStackWidget} [default]
 */

export class HomeLayoutService extends BaseController {
	/** @type {import('gridstack').GridStackWidget[]} */
	layoutSave;

	/** @type {Widget[]} */
	defaultWidgets;

	/** @type {Widget[]} */
	availableWidgets;

	/** @type {import('~/components/home/fast-import/fast-import.service').FastImportService} */
	FastImportService;

	/** @type {Map<string, { element: HTMLElement }>} */
	widgetReferences = new Map();

	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {import('~/components/home/fast-import/fast-import.service').FastImportService} FastImportService
	 */
	constructor(
		$injector,
		FastImportService,
	) {
		super($injector);
		this.FastImportService = FastImportService;

		this.availableWidgets = [];

		this.user = false;

		/**
		 * @type {any[]}
		 * widget ids
		 */
		this.defaultWidgets = [
			{
				type:    'home-dashboard-actions-block',
				id:      'home-dashboard-actions-block',
				title:   'Actions',
				default: {
					h:             14,
					w:             6,
					x:             0,
					y:             0,
					sizeToContent: true,
				},
				params:  {
					isOpen:   true,
					widgetId: 'home-dashboard-actions-block',
				},
			},
			// {
			// 	type: 'home-fastimport-block',
			// 	id: 'home-fastimport-block',
			// 	title: 'Import rapide',
			// 	default: {
			//		// h: 14,
			// 		w: 6,
			// 		x: 6,
			// 		y: 0,
			// 	},
			// },
			{
				type:    'home-bookmark-block',
				id:      'home-bookmark-block',
				title:   'Favoris',
				default: {
					h:             14,
					w:             6,
					x:             6,
					y:             14,
					sizeToContent: true,
				},
				params:  {
					isOpen:   true,
					widgetId: 'home-bookmark-block',
				},
			},
			{
				type:    'home-query-block',
				id:      'home-query-block',
				title:   'Requêtes',
				default: {
					h:             14,
					w:             6,
					x:             6,
					y:             28,
					sizeToContent: true,
				},
				params:  {
					isOpen:   true,
					widgetId: 'home-query-block',
				},
			},
			{
				type:    'home-workflow-block',
				id:      'home-workflow-block',
				title:   'Workflows',
				default: {
					h:             14,
					w:             6,
					x:             6,
					y:             42,
					sizeToContent: true,
				},
				params:  {
					isOpen:   true,
					widgetId: 'home-workflow-block',
				},
			},
		];

		/** @type {?import('gridstack').GridStack} */
		this.gridstack = null;

		this.isEditMode = false;

		this.layoutSave = [];

		this.widgets = [];

	}

	// --- Functions ------------------------------------------------------------

	setEditMode(mode = true) {
		this.isEditMode = mode;

		// @ts-ignore
		this.layoutSave = this.gridstack.save(false, false);

		this.gridstack?.setStatic(!this.isEditMode);
	}

	cancelSave() {
		this.gridstack?.load(this.layoutSave);

		this.setEditMode(false);
	}

	async reset() {
		await this.api.settings.delete({
			id:       SETTINGS_ID,
			node_kid: TARGET_KID,
		});

		await this.api.settings.delete({
			id:       METADATA_ID,
			node_kid: TARGET_KID,
		});

		this.Notification.success({
			title:   'Réinitialisation effectuée',
			message: 'La mise en forme du tableau de bord à été réinitialisée avec succes!',
		});

		if (this.gridstack) {
			this.gridstack?.removeAll();
			await this.onGridReady(this.gridstack);
		}
	}

	/**
	 *
	 * @param {Widget} widget
	 * @returns
	 */
	createWidgetElement(widget) {
		const scope = this.$rootScope.$new(true);

		const parameters = Object
			.entries(widget.params || {})
			.map(([key]) => {
				const kebabKey = kebabCase(key);
				return `${kebabKey}="$ctrl.params['${key}']"`;
			})
			.join(' ');

		// @ts-ignore
		scope.$ctrl = {
			params:  widget.params,
			remove:  () => {
				const foundElement = document.querySelector(`[gs-id="${widget.id}"]`);
				// @ts-ignore
				this.gridstack.removeWidget(foundElement, true);
			},
			service: this,
		};

		// eslint-disable-next-line no-underscore-dangle
		const element_ = `
		<div class="grid-stack-item ${widget.type === 'home-bookmark-block' ? 'show-on-mobile' : ''}">
			<div
				class="grid-stack-item-content"
			>
				<${widget.type}
					${parameters}
					class="gs-content"
				></${widget.type}>
				<div
					ng-if="$ctrl.service.isEditMode"
					class="remove"
					ng-click="$ctrl.remove('${widget.id}')"
				>
					<i class="fa fa-times"></i>
				</div>
			</div>
		</div>`;
		const compiled = this.$compile(element_)(scope);
		// eslint-disable-next-line no-shadow
		const element = compiled.get(0);
		return element;
	}

	/**
	 *
	 * @param {GridStack} reference
	 * @param {any} reference
	 */
	async onGridReady(reference) {
		this.gridstack = reference;

		this.availableWidgets = cloneDeep(this.defaultWidgets);

		this.gridstack.on('change', () => {
			this.$rootScope.$evalAsync();
		});

		/** @type {QueryBuilder<'dashboard'>} */
		const builder = APIV5.instance.createQueryBuilder();

		builder
			.select(select => select
				.addColumn(column => column
					.as('dashboard')
					.withTarget('K_NODE;DASHBOARD')
					.withPath(path => path
						// .addFlow('up', 'K_NODE;WIDGET', undefined, 'END', undefined)
						.addFlow('up', 'K_NODE;DASHBOARD', undefined, 'END', 'K_NODE;COMPOSED_OF', 'K_NODE;WIDGET'),
					),
				),
			)
			.from(from => from.addElement('K_NODE;WIDGET'));

		const response = await builder.fetchAll(undefined, undefined, { id: 'home-layout-service.on-grid-ready' });

		/** @type {Record<string, import('@knitiv/api-client-javascript').QueryCell>} */
		const dashboards = {};

		// For each dashboard
		for (const row of response.resolved().rows) {
			if (row.dashboard) {
				// Iterate over dashboards
				for (const dash of row.dashboard) {
					if (dash.kid && dash.kid !== 'NONE' && !dashboards[dash.kid]) {
						// Merge same dashboards
						dashboards[dash.kid] = dash;
					}
				}
			}
		}

		this.dashboards = cloneDeep(dashboards);

		const dashboardWidgets = Object.entries(dashboards)
			.map(([key, dash]) => {
				const id = `home-dashboard-block-${key}`;
				/** @type {Widget} */
				const widget = {
					type:    'home-dashboard-block',
					id,
					title:   `Tableau de bord: ${dash.name}`,
					params:  {
						dashboard: dash,
						widgetId:  id,
						isOpen:    false,
					},
					default: {
						h: 14,
						w: 6,
					},
				};

				return widget;
			});
		this.availableWidgets.push(...dashboardWidgets);

		// ---

		const settings = await this.api.settings.get({
			id:       SETTINGS_ID,
			node_kid: TARGET_KID, // Home node
		}, { id: 'home-layout-service.on-grid-ready.layout' });

		const metadata = await this.api.settings.get({
			id:       METADATA_ID,
			node_kid: TARGET_KID, // Home node
		}, { id: 'home-layout-service.on-grid-ready.metadata' });

		// this.$timeout(() => {
		this.gridstack.batchUpdate();

		// eslint-disable-next-line no-console
		console.log('settings', settings);
		// eslint-disable-next-line no-console
		console.log('metadata', metadata);

		const metadataMap = new Map(Object.entries(Utils.JSONParseSafe(metadata?.kjson)));

		// eslint-disable-next-line no-console
		console.log('metadataMap', metadataMap);

		// apply settings from the node
		if (settings) {
			/** @type {import('gridstack').GridStackOptions} */
			// eslint-disable-next-line prefer-destructuring
			const dashboard = Utils.JSONParseSafe(settings?.kjson);

			if (dashboard.children) {
				for (const widget of dashboard.children) {
					if (!widget) {
						console.error('No widget !');
						return;
					}

					const foundWidget = this.availableWidgets.find(w => w.id === widget.id);

					if (!foundWidget) {
						console.error(`Unknown widget type: ${widget.id}`);
						// eslint-disable-next-line no-continue
						continue;
					}

					const widgetMetadata = metadataMap.get(foundWidget.id);
					// eslint-disable-next-line no-console
					console.log('widgetMetadata', widgetMetadata);
					foundWidget.params = {
						...foundWidget.params,
						...widgetMetadata,
						widgetId: foundWidget.id,
					};

					const widgetElt = this.createWidgetElement(foundWidget);

					/** @type {any} */
					let entry = [];

					if (dashboard && dashboard.children) {
						entry = dashboard.children.find(child => child.id === widget.id);
					}

					/** @type {import('gridstack').GridStackWidget} */
					let widgetOptions = {
						w: 6,
						// h: 14,
						x:        undefined,
						y:        undefined,
						id:       widget.id,
						noMove:   false,
						noResize: false,
					};

					if (entry) {
						widgetOptions = {
							...widgetOptions,
							...entry,
							h: undefined,
						};
					}

					if (widgetElt && widgetOptions.id) {
						this.widgetReferences.set(widgetOptions.id, {
							element: widgetElt,
							// dashboard: widget.params.dashboard,
						});
						this.$timeout(() => {
							this.gridstack?.addWidget(widgetElt, widgetOptions);
						});
					}
					else {
						throw new Error('Template not compiled');
					}
				}
			}

			if (settings.profil_kid) {
				this.user = settings.profil_kid === this.$rootScope.context?.profil.kid;
			}
		}
		// or generate settings on the fly
		else {
			let ly = 0;
			let ry = 0;
			for (const widget of this.availableWidgets) {
				let skip = false;

				const widgetElt = this.createWidgetElement(widget);

				/** @type {import('gridstack').GridStackWidget} */
				let widgetOptions = {
					w:        6,
					h:        14,
					x:        undefined,
					y:        undefined,
					id:       widget.id,
					noMove:   false,
					noResize: false,
					...widget.default,
				};

				if (widget.type === 'home-dashboard-actions-block') {
					widgetOptions.x = 0;
					widgetOptions.y = ly;

					ly += 14;
				}

				if (widget.type === 'home-fastimport-block') {
					await this.FastImportService.fetch();

					if (this.FastImportService.fastImports.length > 0) {
						widgetOptions.x = 0;
						widgetOptions.y = ly;

						ly += 14;
					}
					else {
						skip = true;
					}
				}

				if (widget.type === 'home-dashboard-block') {
					widgetOptions.x = 0;
					widgetOptions.y = ly;
					widgetOptions.h = 11;

					ly += 11;
				}

				if (widget.type === 'home-bookmark-block') {
					widgetOptions.x = 6;
					widgetOptions.y = ry;

					ry += 14;
				}

				if (widget.type === 'home-query-block') {
					widgetOptions.x = 6;
					widgetOptions.y = ry;

					ry += 14;
				}

				if (widget.type === 'home-workflow-block') {
					widgetOptions.x = 6;
					widgetOptions.y = ry;

					ry += 14;
				}

				widgetOptions = {
					...widgetOptions,
					...widget.default,
				};

				delete widgetOptions.h;

				if (widgetElt) {
					if (skip) {
						console.warn('Skipping widget', widget.id);
					}
					else {
						this.widgetReferences.set(widget.id, {
							element: widgetElt,
						});
						this.$timeout(() => {
							this.gridstack?.addWidget(widgetElt, widgetOptions);
						});
					}
				}
				else {
					throw new Error('Template not compiled');
				}
			}
		}

		this.gridstack.commit();

		this.gridstack.setAnimation(true);
		this.gridstack.margin(4);

		// eslint-disable-next-line angular/interval-service
		// setInterval(() => {
		// for (const [key, value] of [...this.widgetReferences.entries()]) {
		// 	console.log('resize to content', key, value);
		// 	this.gridstack.resizeToContent(value.element);
		// }
		// }, 10000);
		// });

		this.$rootScope.$on('gridtsack.refresh-item', (event, {
			widgetId,
			delay,
		}) => {
			for (const [elementkid, value] of this.widgetReferences.entries()) {
				if (elementkid === widgetId) {
					this.$timeout(() => {
						this.gridstack?.resizeToContent(value.element);
					}, delay ?? 0);
				}
			}
		});
	}

	async saveLayout() {
		this.setEditMode(false);

		/** @type {import('gridstack').GridStackOptions} */
		// @ts-ignore
		const layout = this.gridstack.save(false, true);

		const metadata = new Map();

		for (const [elementkid, value] of this.widgetReferences.entries()) {
			/** @type {any} */
			const scope = element(value.element)
				.scope();

			const openState = scope.$ctrl.params.isOpen;

			metadata.set(elementkid, {
				isOpen: openState,
			});
		}

		// eslint-disable-next-line no-console
		console.log('metadata', metadata);

		await this.api.settings.set({
			id:       SETTINGS_ID,
			profil:   this.user ? this.$rootScope.context?.profil.kid : undefined,
			node_kid: TARGET_KID,
			kjson:    JSON.stringify(layout),
		}, { id: 'home-layout-service.save-layout' });

		await this.api.settings.set({
			id:       METADATA_ID,
			profil:   this.user ? this.$rootScope.context?.profil.kid : undefined,
			node_kid: TARGET_KID,
			kjson:    JSON.stringify(Object.fromEntries(metadata)),
		}, { id: 'home-layout-service.save-metadata' });

		this.Notification.success({
			title:   'Sauvegarde effectuée',
			message: 'La mise en forme du tableau de bord à été enregistrée avec succes!',
		});
	}
}
